import { useCallback, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { useLazyQuoteApi } from 'Shared/services/Api';
import {
  isRateUwFailType,
  RateType,
  RateUwFailType,
  transformRateResponse,
} from 'QuoteApp/services/endpoints/GetRate';
import { GetDetailsResponseType } from 'QuoteApp/services/endpoints/GetDetails';

export type RateProviderType = {
  error: boolean;
  loading: boolean;
  rate: RateType | null;
  refreshRate(): void;
  fetchRate(): Promise<RateType | null>;
};

export const useRate = (
  appId: string,
  refreshDetails: () => Promise<GetDetailsResponseType>
): RateProviderType => {
  const match = useRouteMatch<{ appId: string; path?: string }>(
    '/v2/quote-app/:appId/:path?'
  );
  const [stale, setStale] = useState(false);
  const [{ data, loading, error }, fetch] = useLazyQuoteApi<
    RateType | RateUwFailType | null
  >({
    method: 'POST',
    url: `/insurance_applications/${appId}/rate`,
    transformResponse: transformRateResponse,
  });

  const fetchRate = useCallback((): Promise<RateType | null> => {
    return fetch()
      .then(async (response) => {
        const rate = response.data;
        if (rate && isRateUwFailType(rate)) {
          // application status is likely 'stop' at this point
          // refreshDetails to get to the proper stop screen with reasons
          await refreshDetails();
          return null;
        } else {
          return rate;
        }
      })
      .catch(() => {
        // Rate can fail for various reasons without affecting the app state, this error should not show to the user
        return null;
      });
  }, [fetch, refreshDetails]);

  useEffect(() => {
    if (
      appId &&
      stale &&
      !loading &&
      match?.params.path &&
      !['start', 'stalled'].includes(match.params.path)
    ) {
      setStale(false);

      fetchRate();
    }
  }, [appId, loading, match, fetch, stale, fetchRate]);

  return {
    error: !!(error || (data && isRateUwFailType(data))),
    loading: loading,
    rate: data && !isRateUwFailType(data) && !error ? data : null,
    refreshRate: () => {
      setStale(true);
    },
    fetchRate,
  };
};
